/**
 * Batches hex tile load completions and dispatches once per frame to reduce
 * re-renders when many tiles load during pan/scroll.
 * Blob revokes are deferred until idle (no pan, tiles loaded, animation done + buffer) to avoid UI jank.
 */

import { getGlobalDispatch, getGlobalState } from "../State/Context";
import { ActionType } from "../State/State";

const DEFERRED_REVOKE_MS = 1200; // after pan stopped, tiles loaded, 300ms animation + buffer

type PendingTile = {
  x: number;
  y: number;
  objectURL: string | null;
  z: number;
  isEmpty: boolean;
};

let pending: PendingTile[] = [];
let flushScheduled = false;

let pendingRevoke: string[] = [];
let revokeTimerId: ReturnType<typeof setTimeout> | null = null;

function runDeferredRevoke() {
  revokeTimerId = null;
  if (pendingRevoke.length === 0) return;
  const urls = pendingRevoke;
  pendingRevoke = [];
  for (const url of urls) {
    try {
      URL.revokeObjectURL(url);
    } catch (_) {}
  }
}

function scheduleDeferredRevoke(urls: string[]) {
  if (urls.length === 0) return;
  pendingRevoke.push(...urls);
  if (revokeTimerId !== null) clearTimeout(revokeTimerId);
  revokeTimerId = setTimeout(runDeferredRevoke, DEFERRED_REVOKE_MS);
}

/** Call when a blob should be revoked after idle (e.g. hex unmounted with pending blob). */
export function scheduleRevokeBlob(url: string): void {
  if (!url || !url.startsWith("blob:")) return;
  scheduleDeferredRevoke([url]);
}

/** Call when pan/scroll has stopped so revoke runs after idle delay from this moment. */
export function notifyPanStopped(): void {
  if (pendingRevoke.length > 0 && revokeTimerId !== null) {
    clearTimeout(revokeTimerId);
    revokeTimerId = setTimeout(runDeferredRevoke, DEFERRED_REVOKE_MS);
  }
}

function flush() {
  flushScheduled = false;
  if (pending.length === 0) return;
  const state = getGlobalState();
  const cache = state.hexTileCache;
  const toRevoke: string[] = [];
  for (const t of pending) {
    const key = `${t.x}-${t.y}`;
    const existing = cache[key];
    if (existing?.objectURL && existing.objectURL.startsWith("blob:")) {
      toRevoke.push(existing.objectURL);
    }
  }
  const batch = [...pending];
  pending = [];
  getGlobalDispatch()({
    type: ActionType.BATCH_HEX_TILES_LOADED,
    tiles: batch,
  });
  scheduleDeferredRevoke(toRevoke);
}

export function reportHexTileLoaded(
  x: number,
  y: number,
  data: { objectURL: string | null; z: number; isEmpty: boolean }
): void {
  pending.push({
    x,
    y,
    objectURL: data.objectURL,
    z: data.z,
    isEmpty: data.isEmpty,
  });
  if (!flushScheduled) {
    flushScheduled = true;
    requestAnimationFrame(flush);
  }
}
